import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { all as getCountries } from 'country-codes-list';
import { sortBy } from 'lodash';
import { FIRM_FIELDS_CONSTRAINTS, REQUIRES_2FA } from 'common/constants/firms';
import { CustomTextField, WebsiteTextField } from 'components';
import { useFormValidation } from 'services/hooks/useFormValidation';
import { CountryOption, FirmFormProps, FirmFormState, FormValueFirmForm } from './FirmForm.props';
import { classes, StyledGrid } from './FirmForm.styles';



const FirmForm = ({ defaultFormState: { values, errors, dbErrors }, onFormChange }: FirmFormProps) => {
  const { formValidated, validateForm } = useFormValidation(FIRM_FIELDS_CONSTRAINTS);
  const [formValues, setFormValues] = useState<FormValueFirmForm>(values);
  const countries = getCountries().map(({ countryCode, countryNameEn }) => ({
    value: countryCode,
    label: countryNameEn,
  }));

  useEffect(() => {
    validateForm(formValues);
  }, [validateForm, formValues]);

  useEffect(() => {
    onFormChange(formValidated as FirmFormState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValidated]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFormValues(prev => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }, []);

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setFormValues(prev => ({
      ...prev,
      requires_2fa: event.target.checked,
    }));
  }, []);

  const handleCountryChange = useCallback((__event: React.ChangeEvent<object>, option: CountryOption | null) => {
    if (option) {
      setFormValues(prev => ({
        ...prev,
        country: option.value,
      }));
    }
  }, []);

  const countryValue = useMemo<CountryOption | undefined>(() => {
    if (formValues?.country) {
      return countries.find(c => c.value === formValues.country);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  return (
    <StyledGrid container display="block">
      <CustomTextField
        fullWidth
        required
        id="name"
        type="text"
        name="name"
        label="Name"
        value={(formValues.name as string) ?? ''}
        className={classes.margin}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <CustomTextField
        fullWidth
        id="address"
        type="text"
        name="address"
        label="Street Address"
        value={(formValues.address as string) ?? ''}
        className={classes.margin}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <Grid container spacing={1}>
        <Grid size={{ md: 4 }} className={classes.margin}>
          <Autocomplete
            id="country"
            options={sortBy(countries, 'label')}
            style={{ width: '100%' }}
            autoHighlight
            getOptionLabel={option => option.label}
            onChange={handleCountryChange}
            value={countryValue}
            classes={{
              popper: classes.autocomplete,
            }}
            renderInput={params => <TextField {...params} fullWidth required label="Choose a country" name="country" />}
            disableClearable
          />
        </Grid>
        <Grid size={{ md: 4 }} className={classes.margin}>
          <CustomTextField
            fullWidth
            id="state"
            type="text"
            name="state"
            label="State"
            value={(formValues.state as string) ?? ''}
            onChange={handleChange}
            formErrors={errors}
            dbErrors={dbErrors}
          />
        </Grid>
        <Grid size={{ md: 4 }} className={classes.margin}>
          <CustomTextField
            fullWidth
            id="zip_code"
            type="text"
            name="zip_code"
            label="Zip Code"
            value={(formValues.zip_code as string) ?? ''}
            onChange={handleChange}
            formErrors={errors}
            dbErrors={dbErrors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.checkbox}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox checked={(formValues?.requires_2fa as boolean) ?? false} onChange={handleCheckboxChange} />
            }
            label={REQUIRES_2FA}
          />
        </FormGroup>
      </Grid>
      <Typography variant="h4" component="h3" className={classes.formHeader}>
        Contact information
      </Typography>
      <CustomTextField
        fullWidth
        id="phone_number"
        type="text"
        name="phone_number"
        label="Phone number"
        value={(formValues.phone_number as string) ?? ''}
        className={classes.margin}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <WebsiteTextField
        fullWidth
        id="website"
        type="text"
        name="website"
        label="Website"
        value={formValues.website ?? ''}
        className={classes.margin}
        placeholder="http(s)://www.example.com"
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
    </StyledGrid>
  );
};

export default FirmForm;
